<template>
  <div>
    <AppHeader />
    <div class="takeaway-container">
      <h2>Takeaway Customer Setting</h2>
      
      <div class="setting-box">
        <label for="takeaway-customer-toggle">Enable Takeaway Customer:</label>
        <div class="toggle-switch">
          <input 
            type="checkbox" 
            id="takeaway-customer-toggle" 
            v-model="takeawayCustomer" 
            :true-value="1"
            :false-value="0"
          >
          <span class="slider round"></span>
        </div>
        <p class="setting-description">
          When enabled, customers will be able to select takeaway option during checkout.
        </p>
      </div>

      <button @click="updateTakeawaySetting" :disabled="isUpdating">
        {{ isUpdating ? 'Updating...' : 'Update Setting' }}
      </button>
      <p v-if="message" :class="['message', messageType]">{{ message }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TakeawayPopupComponent',
  data() {
    return {
      takeawayCustomer: 0,
      isUpdating: false,
      message: '',
      messageType: '',
      appId: null,
      isLoading: false
    };
  },
  mounted() {
    this.getAppIdFromStorage();
    this.fetchTakeawaySetting();
  },
  methods: {
    getAppIdFromStorage() {
      this.appId = localStorage.getItem('appId');
      if (!this.appId) {
        this.showMessage('App ID not found in localStorage', 'error');
      }
    },
    
    async fetchTakeawaySetting() {
      if (!this.appId) return;
      
      this.isLoading = true;
      this.$root.isLoading = true;
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/apps/${this.appId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.data) {
          // Set the takeaway customer value (default to 0 if not set)
          this.takeawayCustomer = data.data.takeaway_customer || 0;
        }
      } catch (error) {
        console.error('Error fetching takeaway setting:', error);
        this.showMessage('Failed to load takeaway setting', 'error');
      } finally {
        this.isLoading = false;
        this.$root.isLoading = false;
      }
    },

    async updateTakeawaySetting() {
      if (!this.appId) {
        this.showMessage('App ID is missing', 'error');
        return;
      }

      this.isUpdating = true;
      this.message = '';
      const token = localStorage.getItem('authToken');

      try {
        const response = await fetch(`${this.$BASE_API_URL}/api/apps/${this.appId}/takeaway`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            takeaway_customer: this.takeawayCustomer
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const successMessage = data.message || 'Takeaway setting updated successfully!';
        this.showMessage(successMessage, 'success');
        
        // Update global settings if needed
        if (this.$globalAppSettings) {
          this.$globalAppSettings.takeawayCustomer = this.takeawayCustomer;
        }
        
      } catch (error) {
        console.error('Error updating takeaway setting:', error);
        let errorMsg = 'Failed to update takeaway setting';
        
        if (error.response) {
          errorMsg = error.response.data?.message || 
                    error.response.data?.error || 
                    error.response.statusText;
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        this.showMessage(errorMsg, 'error');
      } finally {
        this.isUpdating = false;
      }
    },

    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;
      setTimeout(() => {
        this.message = '';
        this.messageType = '';
      }, 5000);
    }
  }
};
</script>

<style scoped>
.takeaway-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
}

h2 {
  text-align: center;
  margin-bottom: 2rem;
  color: #333;
}

.setting-box {
  border: 2px solid #ddd;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  background-color: #f9f9f9;
}

label {
  display: block;
  margin-bottom: 1rem;
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
}

.setting-description {
  margin-top: 1rem;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Toggle Switch Styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #4CAF50;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

button {
  padding: 12px 30px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
  display: block;
  margin: 0 auto;
}

button:hover {
  background-color: #45a049;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.message {
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
}

.message.success {
  background-color: #dff0d8;
  color: #3c763d;
  border: 1px solid #d6e9c6;
}

.message.error {
  background-color: #f2dede;
  color: #a94442;
  border: 1px solid #ebccd1;
}
</style>